import dynamic from 'next/dynamic';
import React, { memo, useContext, useRef, useState } from 'react';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { type ComparePayload, type CompareResponse } from '@/app/api/search/compare/createCompareData.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import DebugBanner from '@/app/components/search/components/Banners/Debug/DebugBanner.tsx';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import TitleLayout from '@/app/components/search/components/LayoutManager/TitleLayout';
import TaxesIncludedModal from '@/app/components/search/components/Modal/Modals/TaxesIncludedModal';
import { useSearchPageData } from '../../../searchPageDataProvider.tsx';
import type Form from '../../Form';
import { APIStatusEnum } from '../../../common/reloadData/APIStatusEnum';
import { RouteType } from '../../../Curiosity';
import { getListContainerProps } from './utils';
import CardsGrid from './CardsGrid';
import Adv from '../../../../../common/biff/searchPage/AdvSearchPage';
import Disclaimer from '../../Disclaimer';
import { FiltersBarContainer, ListLayoutStyled } from './styled';
import getSearchParams from '../../../common/getSearchParams';
import type { DynamicFiltersResponse } from '../../../utils/api/dynamicFiltersResponseTypes';
import OpxFlexibleBanner from '../../Banners/OpxFlexibleBanner';
import FlexibleDatesSwitcher from '../../Banners/FlexibleDatesSwitcher';
import { createShowVoucherExtraBanner } from '../../Banners/VoucherExtraBanner/createShowVoucherExtraBanner';
import { SearchPageContext } from '../SearchPageContext.ts';
import { waitingCallGeneric } from '../../../common/waitingCallGeneric.ts';
import clientAPICall from '../../../common/clientAPICall.ts';
import { useCanShowModal } from '../../../hooks/useCanShowModal.ts';
import { removeAllFilters } from '../../Filters/utils.ts';

const Pager = dynamic(() => import('../../Pager'), { ssr: false });
const EvoPager = dynamic(() => import('../../Pager/EvoPager'), { ssr: false });
const NoResults = dynamic(() => import('../../NoResults'), { ssr: false });
const VoucherExtraBanner = dynamic(() => import('../../Banners/VoucherExtraBanner'), { ssr: false });
const ComparisonToast = dynamic(() => import('../../Comparison/ComparisonToast'), { ssr: false });
const ComparisonModal = dynamic(() => import('../../Modal/Modals/ComparisonModal'), { ssr: false });

export type ListContainerProps = {
  listContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  routeType: RouteType;
  offset: number;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  verticalFiltersRef: React.MutableRefObject<Form | undefined>;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  setCardOverHotelId: React.Dispatch<React.SetStateAction<number | undefined>>;
};

const ListContainer = memo(
  ({ listContainerRef, offset, dynamicFiltersResponse, setOffset, setCardOverHotelId }: ListContainerProps) => {
    const isClient = useIsClient();
    const { searchInfoData, localization, commonData } = usePageWrapperData();
    const { productsData, isLoading } = useSearchPageData();
    const { searchMode } = searchInfoData;
    const { t } = useTranslation();
    const { isMobile, isDesktop, isTablet, handlePagination } = useCommonClient();
    const { listView, dealsToCompare, handleCompareCallFinished, triggerTaxesIncludedTooltip } =
      useContext(SearchPageContext);
    const { curiosityClickEvents } = useCuriosityManager();
    const isWaiting = productsData.apiStatus === APIStatusEnum.WAIT;
    const { configValuesApp } = localization;
    const comparisonEnabled = localization?.configValuesApp?.comparisonEnabled && searchInfoData.isLiveSearch;
    const evolutionResultsOnlyEnabled = localization?.configValuesApp?.evolutionResultsOnlyEnabled;
    const cardsGridRef = useRef<HTMLDivElement>(undefined);
    const [comparisonModalIsLoading, setComparisonModalIsLoading] = useState(false);
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    const { canShowModal: canShowComparisonModal } = useCanShowModal(showComparisonModal);
    const enableComparisonToast = comparisonEnabled && dealsToCompare.length > 0;
    const enableComparisonModal = canShowComparisonModal && enableComparisonToast && !isWaiting && !isLoading;

    const getCompareCall = (hotelIds: number[]) => () =>
      clientAPICall<ComparePayload, CompareResponse>({
        apiName: API_NAME.COMPARE,
        payload: {
          searchParams: removeAllFilters(getSearchParams()),
          searchInfoData,
          commonData,
          localization,
          hotelIds,
        },
        commonData,
        traceParent: searchInfoData.searchTraceParent,
      });

    const handleCompareModalClosed = () => {
      curiosityClickEvents.comparisonModalClose();
      setShowComparisonModal(false);
    };

    const handleCompareOpenClicked = async () => {
      if (dealsToCompare.length > 1) {
        curiosityClickEvents.comparisonModalOpen();
        setShowComparisonModal(true);
        setComparisonModalIsLoading(true);
        const compareCall = getCompareCall(dealsToCompare.map((d) => d.hotelId));
        waitingCallGeneric<CompareResponse>({
          waitingAPICall: compareCall,
          handleWaitingCallFinished: (response) => {
            setComparisonModalIsLoading(false);
            handleCompareCallFinished(response);
          },
        });
      }
    };

    const {
      hasNoResults,
      hideFirstDisclaimer,
      firstDisclaimerText,
      hideSecondDisclaimer,
      secondDisclaimerText,
      hideTshDisclaimer,
      hideMypriceDisclaimer,
      listContainerClassName,
      pagerProps,
    } = getListContainerProps({
      dealCards: productsData.dealCards || [],
      dealsCount: productsData.productCounters?.productsCount || 0,
      searchMode,
      localization,
      isDesktop,
      isTablet,
      offset,
      isLoading,
      handlePagination,
      setOffset,
      t,
    });

    const showVoucherExtraBanner = createShowVoucherExtraBanner({ searchInfoData, productsData, localization });
    // MPH-945: the apex switcher and the OPX banner both offer nearby dates, so only one of them
    // shows. The switcher wins: it is the general control, and it reports the window it applied.
    // The `apex` response flag is the last gate: the config flag only says the search was *sent* to
    // apex, and apex is allowed to fall back to Vacation Catalog, which ignores apex_flexible_days.
    // Without this the row would offer — and report — a window that was never applied.
    const showFlexibleDatesSwitcher = !!configValuesApp?.apexFlexibleDatesEnabled && !!productsData?.flags?.apex;
    const showOpxFlexibleBanner =
      !showFlexibleDatesSwitcher && localization?.configValuesApp?.opxFlexible && productsData?.flags?.opxFlexibleDates;
    const hasResults = productsData?.dealCards && productsData?.dealCards?.length > 0;

    return (
      <ListLayoutStyled
        ref={listContainerRef}
        listView={listView}
        isMobile={isMobile}
        isTablet={isTablet}
        className={listContainerClassName}
        evolutionResultsOnlyEnabled={evolutionResultsOnlyEnabled}
      >
        {!evolutionResultsOnlyEnabled && <TitleLayout dynamicFiltersResponse={dynamicFiltersResponse} />}
        {evolutionResultsOnlyEnabled && configValuesApp?.taxesIncludedPopup && (
          <TaxesIncludedModal localization={localization} onModalClose={triggerTaxesIncludedTooltip} />
        )}
        {showOpxFlexibleBanner && <OpxFlexibleBanner />}
        {showVoucherExtraBanner && <VoucherExtraBanner />}
        {showFlexibleDatesSwitcher && <FlexibleDatesSwitcher />}
        <DebugBanner />
        {hasResults || isWaiting || isLoading ? (
          <CardsGrid
            routeType={RouteType.OSMAP}
            cardsGridRef={cardsGridRef}
            isWaiting={isWaiting}
            isLoading={isLoading}
            setCardOverHotelId={setCardOverHotelId}
          />
        ) : (
          <NoResults
            hasNoResults={hasNoResults}
            isLoading={isLoading}
            isWaiting={isWaiting}
            dynamicFiltersResponse={dynamicFiltersResponse}
          />
        )}
        <FiltersBarContainer className="filters-bar-container" />
        {isClient &&
          (configValuesApp?.evolutionResultsOnlyEnabled ? <EvoPager {...pagerProps} /> : <Pager {...pagerProps} />)}
        <Adv id="div-gpt-ad-bottom" isBottom={true} hide={hasNoResults} />
        <Adv id="div-gpt-ad-bottom2" isBottom={true} hide={hasNoResults} />
        <Disclaimer hide={hideFirstDisclaimer} text={firstDisclaimerText} />
        <Disclaimer hide={hideSecondDisclaimer} text={secondDisclaimerText} />
        <Disclaimer
          hide={hideMypriceDisclaimer}
          text={t('vacation.oss.components.searchresults.myprice_pricedisclaimer.footer')}
        />
        <Disclaimer
          hide={hideTshDisclaimer}
          text={t('vacation.oss.components.searchresults.tsh_pricedisclaimer.footer')}
        />
        <Adv id="afs-results-bottom" hide={hasNoResults} />
        {isClient && enableComparisonToast && (
          <ComparisonToast
            handleCompareOpenClicked={handleCompareOpenClicked}
            isWaitingOrLoading={isWaiting || isLoading}
          />
        )}
        {isClient && enableComparisonModal && (
          <ComparisonModal
            isOpen={showComparisonModal}
            handleCompareModalClosed={handleCompareModalClosed}
            isWaiting={comparisonModalIsLoading}
          />
        )}
      </ListLayoutStyled>
    );
  },
  (prevProps: ListContainerProps, nextProps: ListContainerProps) => {
    if (JSON.stringify(prevProps.dynamicFiltersResponse) !== JSON.stringify(nextProps.dynamicFiltersResponse))
      return false;
    return prevProps.offset === nextProps.offset;
  }
);

ListContainer.displayName = 'ListContainer';

export default ListContainer;
